// The accessibility statement of a website: a guided generator in four steps.
//  1. Language: the languages the website's pages are in. One is the primary; the rest are optional.
//  2. Data: the little only the entity knows (name, address, email); the website is filled in from the project.
//  3. Review: SiteNorma generates the statement from the data, the regulatory profile and what the analyses found,
//     and shows it as it will look. The text is read-only: a change goes back to the data and it is generated again.
//  4. Review and approve: the degree of conformity is calculated from the registered evaluation. Approving
//     makes a version (who and when are recorded by the platform) and does not put it on the website:
//     publishing it is a second choice (script, hosted link, HTML or WordPress).
// Approving the document does not change the evaluation and does not certify the website.
import { CheckCircledIcon, CheckIcon } from '@radix-ui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Checkbox, CheckboxGroup, Form } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  can,
  generateStatement,
  meQuery,
  publishStatementOn,
  restoreStatement,
  STATEMENT_LANGS,
  type StatementData,
  type StatementLang,
  type StatementPublicationMethod,
  type StatementView,
  saveStatementData,
  setStatementLanguages,
  siteQuery,
  statementLanguagesQuery,
  statementQuery,
  statementVersionQuery,
  validateStatement,
} from '../api';
import { writeStored } from '../persist';
import { announce, Button, Field, Fold, HelpPopover, Message, usePageTitle } from '../ui';
import { useDate } from './scan-parts';

/** A code to copy, with what it is for. The fold already names it when `hideLabel` is set. */
export function Snippet({
  label,
  hint,
  value,
  hideLabel = false,
}: {
  label: string;
  hint: string;
  value: string;
  hideLabel?: boolean;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <div className="statement-snippet">
      {!hideLabel && <p className="font-bold">{label}</p>}
      <p className="text-sm text-ink-muted">{hint}</p>
      {/* It scrolls: a keyboard has to be able to reach it. */}
      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling area must be reachable by keyboard. */}
      <section className="statement-code" tabIndex={0} aria-label={label}>
        <pre className="m-0 whitespace-pre-wrap">
          <code>{value}</code>
        </pre>
      </section>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          onPress={async () => {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            announce(t('statement.delivery.copied'));
            window.setTimeout(() => setCopied(false), 3000);
          }}
        >
          {t('statement.delivery.copy')}
          <span className="sr-only"> — {label}</span>
        </Button>
        <span role="status" className="text-sm font-bold">
          {copied ? t('statement.delivery.copied') : ''}
        </span>
      </div>
    </div>
  );
}

const STEPS = ['language', 'data', 'review', 'validate'] as const;
type StepId = (typeof STEPS)[number];
type EditorStep = Exclude<StepId, 'language'>;

/** The four steps and where the person is. A step can be opened once the ones before it are done. */
function Stepper({
  step,
  onStep,
  reached,
}: {
  step: StepId;
  onStep: (step: StepId) => void;
  reached: number;
}) {
  const { t } = useTranslation();
  return (
    <ol className="verify-steps statement-stepper">
      {STEPS.map((id, index) => {
        const state = id === step ? 'current' : index < STEPS.indexOf(step) ? 'done' : 'next';
        const open = index <= reached;
        return (
          <li key={id} data-state={state} aria-current={state === 'current' ? 'step' : undefined}>
            <span className="step-mark" aria-hidden="true">
              {index + 1}
            </span>
            {open && id !== step ? (
              <button type="button" className="link-button" onClick={() => onStep(id)}>
                {t(`statement.steps.${id}`)}
              </button>
            ) : (
              <span>{t(`statement.steps.${id}`)}</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** The evaluation and the document, said apart. Approving the text does not change the evaluation. */
function StatusLine({ view }: { view: StatementView }) {
  const { t } = useTranslation();
  const date = useDate();
  const approved = view.status !== 'pendiente';
  const latest = view.published;
  return (
    <p className="statement-status" data-status={view.status} role="status">
      <strong>
        {t('statement.evaluation.label')}: {t(`statement.evaluation.state.${view.evaluation.state}`)}
      </strong>
      <span>
        {' '}
        · {t('statement.document.label')}:{' '}
        {t(approved ? 'statement.document.approved' : 'statement.document.draft')}
      </span>
      {latest && (
        <span className="text-ink-muted">
          {' '}
          ·{' '}
          {t('statement.status.last', {
            n: latest.version,
            date: date(latest.publishedAt, 'long'),
          })}
        </span>
      )}
      {view.changed && latest && <span className="text-ink-muted"> · {t('statement.status.changes')}</span>}
    </p>
  );
}

/** What the registered evidence supports. The status line already says the state; this is the short reason. */
function EvaluationSummary({ view, siteId }: { view: StatementView; siteId: string }) {
  const { t } = useTranslation();
  const state = view.evaluation.state;
  const text =
    state === 'pendiente'
      ? t('statement.evaluation.pendingText')
      : state === 'incompleta'
        ? t(
            view.evaluation.barriers
              ? 'statement.evaluation.incompleteBarriers'
              : 'statement.evaluation.incompleteClear',
          )
        : t(
            view.evaluation.conformity === 'parcial'
              ? 'statement.evaluation.completePartial'
              : 'statement.evaluation.completeUndeclared',
          );
  const scanId = view.evidence.scanId;
  return (
    <div className="statement-todo">
      <p>{text}</p>
      {state === 'incompleta' && scanId && (
        <p>
          <Link
            to="/sites/$siteId/scans/$scanId"
            params={{ siteId, scanId }}
            onClick={() => writeStored(`tab:${scanId}`, 'manual')}
          >
            {t('statement.evaluation.pendingChecks')}
          </Link>
        </p>
      )}
    </div>
  );
}

function VersionRow({
  siteId,
  lang,
  entry,
  mayEdit,
  onRestore,
}: {
  siteId: string;
  lang: StatementLang;
  entry: StatementView['versions'][number];
  mayEdit: boolean;
  onRestore: (version: number) => void;
}) {
  const { t } = useTranslation();
  const date = useDate();
  const [open, setOpen] = useState(false);
  const version = useQuery({ ...statementVersionQuery(siteId, lang, entry.version), enabled: open });
  return (
    <li>
      <p>
        <strong>{t('statement.versions.version', { n: entry.version })}</strong> ·{' '}
        {date(entry.publishedAt, 'long')} · {t('statement.versions.by', { name: entry.validatedBy })}
      </p>
      <div className="flex flex-wrap gap-3">
        <Button variant="secondary" aria-expanded={open} onPress={() => setOpen((value) => !value)}>
          {t(open ? 'statement.versions.hide' : 'statement.versions.view')}
          <span className="sr-only"> {t('statement.versions.version', { n: entry.version })}</span>
        </Button>
        {mayEdit && (
          <Button variant="secondary" onPress={() => onRestore(entry.version)}>
            {t('statement.versions.restore')}
            <span className="sr-only"> {t('statement.versions.version', { n: entry.version })}</span>
          </Button>
        )}
      </div>
      {open && (
        <div className="statement-old">
          {version.isPending ? (
            <p role="status">{t('app.loading')}</p>
          ) : version.isError ? (
            <Message tone="error">{t('errors.generic')}</Message>
          ) : (
            version.data.sections.map((section) => (
              <div key={section.id}>
                <h4 className="font-bold">{section.title}</h4>
                <p className="whitespace-pre-line">{section.body}</p>
              </div>
            ))
          )}
        </div>
      )}
    </li>
  );
}

/** The two ways to put the approved statement on the website: a script or a link, in this statement's language. */
function Publication({
  view,
  mayEdit,
  busy,
  onPublish,
}: {
  view: StatementView;
  mayEdit: boolean;
  busy: string | null;
  onPublish: (method: StatementPublicationMethod) => void;
}) {
  const { t } = useTranslation();
  const date = useDate();
  const delivery = view.delivery;
  const live = view.live;
  const htmlLangs = (view.embedLangs.length > 0 ? view.embedLangs : [view.lang]).filter(
    (code) => delivery?.snippets.htmlByLang?.[code] || code === view.lang,
  );
  const [htmlLang, setHtmlLang] = useState<StatementLang>(view.lang);
  const htmlCode =
    delivery?.snippets.htmlByLang?.[htmlLang] ??
    (htmlLang === view.lang ? delivery?.snippets.html : undefined) ??
    delivery?.snippets.html ??
    '';
  if (!delivery || !view.published) return null;
  const langs = (view.embedLangs.length > 0 ? view.embedLangs : [view.lang])
    .map((code) => t(`statement.languages.${code}`))
    .join(', ');
  const mark = (method: StatementPublicationMethod) =>
    mayEdit && (
      <div className="statement-mark">
        <Button
          variant={live?.method === method ? 'secondary' : 'primary'}
          isPending={busy === `publication-${method}`}
          onPress={() => onPublish(method)}
        >
          {t(`statement.publication.mark.${method}`)}
        </Button>
        {live?.method === method && (
          <p className="statement-live">
            <CheckCircledIcon aria-hidden="true" />
            {t('statement.publication.markedOn', { date: date(live.at, 'long') })}
          </p>
        )}
      </div>
    );
  return (
    <section className="statement-publication" aria-labelledby="decl-publicar">
      <h4 id="decl-publicar" className="text-lg font-bold">
        {t('statement.publication.title')}
      </h4>
      <p className="text-ink-muted">
        {t('statement.publication.intro', {
          n: view.published.version,
          date: date(view.published.publishedAt, 'long'),
        })}
      </p>
      <p className="font-bold">{t('statement.publication.choose')}</p>
      <div className="statement-options">
        <Fold
          name="decl-publish"
          title={
            <>
              {t('statement.delivery.script')}
              <span className="statement-recommended">{t('statement.delivery.recommended')}</span>
            </>
          }
          hint={t('statement.delivery.scriptSummary')}
        >
          <p>{t('statement.publication.languageScript', { list: langs })}</p>
          <p className="text-sm font-bold">{t('statement.delivery.stepCopy')}</p>
          <Snippet
            hideLabel
            label={t('statement.delivery.script')}
            hint={t('statement.delivery.scriptHint')}
            value={delivery.snippets.script}
          />
          <p className="text-sm text-ink-muted">{t('statement.delivery.stepPaste')}</p>
          <p className="text-sm text-ink-muted">{t('statement.delivery.quality')}</p>
          {mark('script')}
        </Fold>
        <Fold
          name="decl-publish"
          title={t('statement.delivery.link')}
          hint={t('statement.delivery.linkSummary')}
        >
          <p>{t('statement.publication.languageLink', { name: t(`statement.languages.${view.lang}`) })}</p>
          <p className="text-sm font-bold">{t('statement.delivery.stepCopyLink')}</p>
          <Snippet
            hideLabel
            label={t('statement.delivery.link')}
            hint={t('statement.delivery.linkHint')}
            value={delivery.snippets.link}
          />
          <p className="text-sm text-ink-muted">{t('statement.delivery.stepPasteLink')}</p>
          {mark('link')}
        </Fold>
        <Fold
          name="decl-publish"
          title={t('statement.delivery.html')}
          hint={t('statement.delivery.htmlSummary')}
        >
          <p>{t('statement.publication.languageHtml')}</p>
          {htmlLangs.length > 1 && (
            <fieldset className="statement-html-langs">
              <legend className="sr-only">{t('statement.delivery.htmlLangs')}</legend>
              {htmlLangs.map((code) => (
                <button
                  key={code}
                  type="button"
                  className="statement-html-lang"
                  aria-pressed={htmlLang === code}
                  lang={code}
                  onClick={() => setHtmlLang(code)}
                >
                  {t(`statement.languages.${code}`)}
                </button>
              ))}
            </fieldset>
          )}
          <p className="text-sm font-bold">
            {t('statement.delivery.stepCopyHtml', { name: t(`statement.languages.${htmlLang}`) })}
          </p>
          <Snippet
            hideLabel
            label={`${t('statement.delivery.html')} (${t(`statement.languages.${htmlLang}`)})`}
            hint={t('statement.delivery.htmlHint')}
            value={htmlCode}
          />
          <p className="text-sm text-ink-muted">{t('statement.delivery.htmlNote')}</p>
          {mark('html')}
        </Fold>
        <Fold
          name="decl-publish"
          title={t('statement.delivery.wordpress')}
          hint={t('statement.delivery.wordpressSummary')}
        >
          {view.wordpress ? (
            <>
              <p>{t('statement.delivery.wordpressHint')}</p>
              <p className="text-sm text-ink-muted">{t('statement.delivery.wordpressNote')}</p>
              {mark('wordpress')}
            </>
          ) : (
            <>
              <p>{t('statement.delivery.wordpressConnect')}</p>
              <p>
                <Link
                  to="/integrations"
                  hash="integracion-wordpress"
                  className="font-bold text-brand underline"
                >
                  {t('statement.delivery.wordpressGo')}
                </Link>
              </p>
            </>
          )}
        </Fold>
      </div>
    </section>
  );
}

function Editor({
  siteId,
  lang,
  initial,
  mayEdit,
  step,
  onStep,
  active,
  focusNonce,
  pendingLangs,
}: {
  siteId: string;
  lang: StatementLang;
  initial: StatementView;
  mayEdit: boolean;
  step: EditorStep;
  onStep: (step: EditorStep) => void;
  /** False while the language step is on screen: the form stays mounted so what was typed is kept. */
  active: boolean;
  focusNonce: number;
  /** Chosen languages that still have no approved version (added after a previous approval, or not ready then). */
  pendingLangs: StatementLang[];
}) {
  const { t } = useTranslation();
  const date = useDate();
  const queryClient = useQueryClient();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState(initial);
  const [data, setData] = useState<StatementData>(initial.data);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const publicSector = view.profile === 'sector-publico';
  const hasText = view.sections.length > 0;

  /** Every change goes through here: one at a time, with its result said in words (never a silent failure). */
  async function run(key: string, task: () => Promise<StatementView>, done: string, then?: () => void) {
    setBusy(key);
    setError(null);
    setSaved('');
    try {
      const next = await task();
      setView(next);
      setData(next.data);
      queryClient.setQueryData(statementQuery(siteId, lang).queryKey, next);
      await queryClient.invalidateQueries({ queryKey: statementLanguagesQuery(siteId).queryKey });
      setSaved(done);
      announce(done);
      then?.();
    } catch (failure) {
      setError(
        failure instanceof ApiError && failure.code === 'incompleta'
          ? t('statement.errors.incomplete')
          : failure instanceof ApiError && failure.status === 403
            ? t('statement.errors.permission')
            : failure instanceof ApiError && failure.code === 'sin-wordpress'
              ? t('statement.errors.noWordpress')
              : t('statement.errors.generic'),
      );
    } finally {
      setBusy(null);
    }
  }

  const set = <K extends keyof StatementData>(key: K, value: StatementData[K]) =>
    setData((before) => ({ ...before, [key]: value }));
  const ev = view.evidence;
  const blockedByFields = view.problems.some((problem) => problem !== 'conformity');
  const blockedByRegime = view.problems.includes('conformity');
  const pendingApproval = pendingLangs.length > 0;
  const needsApproval = view.status === 'pendiente' || pendingApproval;
  const canApprove =
    view.problems.length === 0 && hasText && confirmed && !busy && !view.outdated && needsApproval;
  const alreadyValidated = view.status !== 'pendiente' && !pendingApproval;
  const next = (view.published?.version ?? 0) + 1;
  const pendingList = pendingLangs.map((code) => t(`statement.languages.${code}`)).join(', ');
  const sectionKey = view.sections.map((section) => section.body).join('\u0000');
  const seenSections = useRef(sectionKey);
  const synced = useRef('');

  // Moving between steps puts the keyboard on the step title. The first arrival does not: the page title stays put.
  useEffect(() => {
    if (!active || focusNonce === 0) return;
    const heading = sectionRef.current?.querySelector('h3');
    if (heading instanceof HTMLElement) heading.focus();
  }, [active, focusNonce]);

  // A new text asks for the confirmation again. Approving one version never approves the next.
  useEffect(() => {
    if (seenSections.current === sectionKey) return;
    seenSections.current = sectionKey;
    setConfirmed(false);
  }, [sectionKey]);

  // The draft follows the evaluation. An approved version stays as it was.
  useEffect(() => {
    if (
      !active ||
      (step !== 'review' && step !== 'validate') ||
      !mayEdit ||
      view.sections.length === 0 ||
      busy
    )
      return;
    const stale = view.data.conformity !== view.evaluation.conformity || view.outdated;
    if (!stale) return;
    const key = `${view.evaluation.conformity}:${view.evidence.scanId ?? ''}:${view.outdated}`;
    if (synced.current === key) return;
    synced.current = key;
    const dirty = JSON.stringify(data) !== JSON.stringify(view.data);
    setBusy('generate');
    setError(null);
    void generateStatement(siteId, lang)
      .then((next) => {
        setView(next);
        if (!dirty) setData(next.data);
        queryClient.setQueryData(statementQuery(siteId, lang).queryKey, next);
        const message = t('statement.review.generated');
        setSaved(message);
        announce(message);
      })
      .catch(() => setError(t('statement.errors.generic')))
      .finally(() => setBusy(null));
  }, [
    active,
    step,
    mayEdit,
    busy,
    data,
    siteId,
    lang,
    view.data,
    view.evaluation.conformity,
    view.outdated,
    view.evidence.scanId,
    view.sections.length,
    queryClient,
    t,
  ]);

  const enforcementField = (
    <Field
      label={t('statement.data.enforcement')}
      description={t(publicSector ? 'statement.data.enforcementRequired' : 'statement.data.enforcementHint')}
      multiline
      value={data.enforcement}
      onChange={(v) => set('enforcement', v)}
      maxLength={2000}
      isRequired={publicSector}
      markRequired={publicSector}
    />
  );

  return (
    <div className="statement-editor" ref={sectionRef}>
      {error && <Message tone="error">{error}</Message>}
      <p role="status" className="font-bold text-ok empty:hidden">
        {saved && (
          <span className="inline-flex items-center gap-1">
            <CheckCircledIcon aria-hidden="true" /> {saved}
          </span>
        )}
      </p>

      {step === 'data' && (
        <section className="statement-step" aria-labelledby="decl-paso-1">
          <h3 id="decl-paso-1" tabIndex={-1}>
            {t('statement.data.title')}
          </h3>
          <p className="text-ink-muted">{t('statement.data.intro')}</p>
          <Form
            validationBehavior="native"
            className="flex flex-col gap-6"
            onSubmit={(event) => {
              event.preventDefault();
              run(
                'data',
                () => saveStatementData(siteId, lang, data),
                t('statement.data.generated'),
                () => onStep('review'),
              );
            }}
          >
            <fieldset className="statement-fields" disabled={!mayEdit}>
              <legend className="font-bold">{t('statement.data.entity')}</legend>
              <Field
                label={t('statement.data.name')}
                value={data.entity.name}
                onChange={(v) => set('entity', { ...data.entity, name: v })}
                maxLength={200}
                isRequired
                markRequired
              />
              <Field
                label={t('statement.data.address')}
                value={data.entity.address}
                onChange={(v) => set('entity', { ...data.entity, address: v })}
                maxLength={300}
                isRequired
                markRequired
              />
              <Field
                label={t('statement.data.email')}
                description={t('statement.data.emailHint')}
                type="email"
                value={data.entity.email}
                onChange={(v) => set('entity', { ...data.entity, email: v })}
                maxLength={200}
                isRequired
                markRequired
              />
            </fieldset>
            <fieldset className="statement-fields" disabled={!mayEdit}>
              <legend className="font-bold">{t('statement.data.website')}</legend>
              <p className="text-sm text-ink-muted">{t('statement.data.prefilled')}</p>
              <Field
                label={t('statement.data.siteName')}
                value={data.site.name}
                onChange={(v) => set('site', { ...data.site, name: v })}
                maxLength={200}
              />
              <Field
                label={t('statement.data.siteUrl')}
                value={data.site.url}
                onChange={(v) => set('site', { ...data.site, url: v })}
                maxLength={500}
                isRequired
                markRequired
              />
            </fieldset>
            {publicSector && (
              <fieldset className="statement-fields" disabled={!mayEdit}>
                <legend className="font-bold">{t('statement.data.guidedTitle')}</legend>
                {enforcementField}
              </fieldset>
            )}
            <Fold title={t('statement.data.advanced')} hint={t('statement.data.advancedHint')}>
              <fieldset className="statement-fields" disabled={!mayEdit}>
                <Field
                  label={t('statement.data.taxId')}
                  value={data.entity.taxId}
                  onChange={(v) => set('entity', { ...data.entity, taxId: v })}
                  maxLength={40}
                />
                <Field
                  label={t('statement.data.phone')}
                  value={data.entity.phone}
                  onChange={(v) => set('entity', { ...data.entity, phone: v })}
                  maxLength={60}
                />
                <Field
                  label={t('statement.data.contactEmail')}
                  description={t('statement.data.contactEmailHint')}
                  type="email"
                  value={data.contact.email}
                  onChange={(v) => set('contact', { ...data.contact, email: v })}
                  maxLength={200}
                />
                <Field
                  label={t('statement.data.contactUrl')}
                  value={data.contact.url}
                  onChange={(v) => set('contact', { ...data.contact, url: v })}
                  maxLength={500}
                />
                <Field
                  label={t('statement.data.responseDays')}
                  inputMode="numeric"
                  value={data.contact.responseDays === null ? '' : String(data.contact.responseDays)}
                  onChange={(v) => {
                    const n = Number.parseInt(v, 10);
                    set('contact', {
                      ...data.contact,
                      responseDays: Number.isFinite(n) && n >= 1 && n <= 90 ? n : null,
                    });
                  }}
                />
                <Field
                  label={t('statement.data.scope')}
                  description={t('statement.data.scopeHint')}
                  multiline
                  value={data.scope}
                  onChange={(v) => set('scope', v)}
                  maxLength={1000}
                />
                <Field
                  label={t('statement.data.exceptions')}
                  description={t('statement.data.exceptionsHint')}
                  multiline
                  value={data.exceptions}
                  onChange={(v) => set('exceptions', v)}
                  maxLength={2000}
                />
                {!publicSector && enforcementField}
                <Field
                  label={t('statement.data.preparationNote')}
                  multiline
                  value={data.preparationNote}
                  onChange={(v) => set('preparationNote', v)}
                  maxLength={2000}
                />
              </fieldset>
            </Fold>
            {mayEdit ? (
              <div>
                <Button type="submit" isPending={busy === 'data'}>
                  {t(hasText ? 'statement.data.regenerate' : 'statement.data.generate')}
                </Button>
              </div>
            ) : (
              <p>{t('statement.readOnly')}</p>
            )}
          </Form>
        </section>
      )}

      {step === 'review' && (
        <section className="statement-step" aria-labelledby="decl-paso-2">
          <h3 id="decl-paso-2" tabIndex={-1}>
            {t('statement.review.title')}
          </h3>
          <StatusLine view={view} />
          <p className="text-ink-muted">{t('statement.review.intro', { standard: view.standard })}</p>
          <div className="statement-evidence">
            <div>
              <h4 className="font-bold">{t('statement.evidence.auto')}</h4>
              {ev.scanDate ? (
                <p>
                  {t('statement.evidence.autoValue', {
                    date: date(ev.scanDate, 'short'),
                    pages: ev.pagesAnalysed,
                    problems: ev.problems,
                  })}
                </p>
              ) : (
                <p>{t('statement.evidence.noScan')}</p>
              )}
            </div>
            <div>
              <h4 className="font-bold">{t('statement.evidence.manual')}</h4>
              <p>
                {t('statement.evidence.manualValue', {
                  registered: ev.manual.registered,
                  total: ev.manual.total,
                })}
              </p>
            </div>
          </div>
          {view.outdated && mayEdit && (
            <Message tone="error">
              {t('statement.evidence.outdated')}{' '}
              <Button
                variant="secondary"
                isPending={busy === 'generate'}
                onPress={() =>
                  run('generate', () => generateStatement(siteId, lang), t('statement.review.generated'))
                }
              >
                {t('statement.review.update')}
              </Button>
            </Message>
          )}
          {view.preview ? (
            <section
              className="statement-preview"
              aria-label={t('statement.review.previewLabel')}
              // The platform wrote this HTML from escaped text (renderStatementFragment); nothing the browser
              // fetched or the person typed reaches it unescaped.
              // biome-ignore lint/security/noDangerouslySetInnerHtml: generated and escaped by the platform.
              dangerouslySetInnerHTML={{ __html: view.preview }}
            />
          ) : (
            <p>{t('statement.review.empty')}</p>
          )}
          <p className="text-sm text-ink-muted">{t('statement.review.readOnly')}</p>
          <div className="flex flex-wrap gap-3">
            <Button variant="secondary" onPress={() => onStep('data')}>
              {t('statement.review.edit')}
            </Button>
            <Button onPress={() => onStep('validate')} isDisabled={!hasText}>
              {t('statement.review.continue')}
            </Button>
          </div>
        </section>
      )}

      {step === 'validate' && (
        <section className="statement-step" aria-labelledby="decl-paso-3">
          <h3 id="decl-paso-3" tabIndex={-1}>
            {t('statement.validate.title')}
          </h3>
          <StatusLine view={view} />
          <EvaluationSummary view={view} siteId={siteId} />
          {!mayEdit && <p>{t('statement.readOnly')}</p>}
          {/* Approve when this language is a draft, or when other chosen languages still have none. */}
          {mayEdit && needsApproval && (
            <>
              {pendingApproval && view.status !== 'pendiente' && (
                <Message tone="error">
                  {t('statement.validate.pendingLangs', { list: pendingList, count: pendingLangs.length })}
                </Message>
              )}
              {view.outdated && (
                <Message tone="error">
                  {t('statement.evidence.outdated')}{' '}
                  <Button
                    variant="secondary"
                    isPending={busy === 'generate'}
                    onPress={() =>
                      run('generate', () => generateStatement(siteId, lang), t('statement.review.generated'))
                    }
                  >
                    {t('statement.review.update')}
                  </Button>
                </Message>
              )}
              {blockedByFields && (
                <p>
                  {t('statement.validate.blocked')}{' '}
                  <button type="button" className="link-button" onClick={() => onStep('data')}>
                    {t('statement.validate.fixData')}
                  </button>
                </p>
              )}
              {blockedByRegime && (
                <p>
                  {t('statement.validate.regime')}{' '}
                  {view.evaluation.state !== 'completada' && ev.scanId ? (
                    <Link
                      to="/sites/$siteId/scans/$scanId"
                      params={{ siteId, scanId: ev.scanId }}
                      onClick={() => writeStored(`tab:${ev.scanId}`, 'manual')}
                    >
                      {t('statement.evaluation.pendingChecks')}
                    </Link>
                  ) : view.evaluation.state === 'pendiente' ? (
                    <Link to="/sites/$siteId" params={{ siteId }}>
                      {t('statement.validate.openAnalysis')}
                    </Link>
                  ) : (
                    <button type="button" className="link-button" onClick={() => onStep('review')}>
                      {t('statement.validate.seeText')}
                    </button>
                  )}
                </p>
              )}
              <label className="follow-check statement-confirm">
                <input
                  id="decl-aprobar"
                  type="checkbox"
                  checked={confirmed}
                  onChange={(event) => setConfirmed(event.target.checked)}
                />
                <span>
                  {pendingApproval && view.status !== 'pendiente'
                    ? t('statement.validate.confirmPending')
                    : t('statement.validate.confirm')}
                </span>
              </label>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  isDisabled={!canApprove}
                  isPending={busy === 'validate'}
                  onPress={() =>
                    run(
                      'validate',
                      () => validateStatement(siteId, lang),
                      pendingApproval && view.status !== 'pendiente'
                        ? t('statement.validate.donePending', { list: pendingList })
                        : t('statement.validate.done', { n: next }),
                      () => setConfirmed(false),
                    )
                  }
                >
                  {pendingApproval && view.status !== 'pendiente'
                    ? t('statement.validate.actionPending')
                    : t('statement.validate.action')}
                </Button>
                <Button variant="secondary" onPress={() => onStep('review')}>
                  {t('statement.validate.back')}
                </Button>
              </div>
              <p className="text-sm text-ink-muted">{t('statement.validate.clarification')}</p>
            </>
          )}
          {view.published && (
            <>
              {view.changed && (
                <Message tone="error">
                  {t('statement.validate.unvalidatedChanges', { n: view.published.version })}
                </Message>
              )}
              <Publication
                view={view}
                mayEdit={mayEdit}
                busy={busy}
                onPublish={(method) =>
                  run(
                    `publication-${method}`,
                    () => publishStatementOn(siteId, lang, method),
                    t(`statement.publication.saved.${method}`),
                  )
                }
              />
            </>
          )}
          {alreadyValidated && mayEdit && (
            <div>
              <Button variant="secondary" onPress={() => onStep('data')}>
                {t('statement.versions.modify')}
              </Button>
            </div>
          )}
        </section>
      )}

      {view.versions.length > 0 && (
        <section aria-labelledby="decl-versiones" className="statement-versions">
          <h3 id="decl-versiones" className="text-lg font-bold">
            {t('statement.versions.title')}
          </h3>
          <p className="text-sm text-ink-muted">{t('statement.versions.hint')}</p>
          <ul>
            {view.versions.map((entry) => (
              <VersionRow
                key={entry.version}
                siteId={siteId}
                lang={lang}
                entry={entry}
                mayEdit={mayEdit}
                onRestore={(version) =>
                  run(
                    'restore',
                    () => restoreStatement(siteId, lang, version),
                    t('statement.versions.restored', { n: version }),
                    () => onStep('review'),
                  )
                }
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function isStatementLang(code: string): code is StatementLang {
  return (STATEMENT_LANGS as readonly string[]).includes(code);
}

function uniqueLangs(codes: readonly StatementLang[]): StatementLang[] {
  return [...new Set(codes)];
}

function sameLangs(a: readonly string[], b: readonly string[]) {
  return a.length === b.length && a.every((code) => b.includes(code));
}

/**
 * The first step: which languages the statement will exist in. Several can be selected; the data in the next
 * steps is filled in once and generates every chosen language.
 */
function LanguageStep({
  detectedCount,
  detected,
  selected,
  approved,
  statusOf,
  mayEdit,
  saving,
  unsupported,
  error,
  focusNonce,
  onChange,
  onNext,
}: {
  detectedCount: number;
  detected: ReadonlySet<StatementLang>;
  selected: StatementLang[];
  /** Languages that already have an approved version (removing one deletes that declaration). */
  approved: ReadonlySet<StatementLang>;
  statusOf: (code: StatementLang) => string;
  mayEdit: boolean;
  saving: boolean;
  unsupported: string[];
  error: string | null;
  focusNonce: number;
  onChange: (langs: StatementLang[]) => void;
  onNext: () => void;
}) {
  const { t } = useTranslation();
  const heading = useRef<HTMLHeadingElement>(null);
  const list = selected.map((code) => t(`statement.languages.${code}`)).join(', ');
  const removingApproved = [...approved].some((code) => !selected.includes(code));
  useEffect(() => {
    if (focusNonce === 0) return;
    heading.current?.focus();
  }, [focusNonce]);

  return (
    <section className="statement-step" aria-labelledby="decl-paso-idioma">
      <h3 id="decl-paso-idioma" ref={heading} tabIndex={-1}>
        {t('statement.language')}
      </h3>
      <div className="lang-prompt">
        <p id="decl-idiomas">
          {detectedCount > 0 ? t('statement.languages.prompt') : t('statement.languages.noneDetected')}
        </p>
        <HelpPopover label={t('statement.languages.helpLabel')}>
          <p>{t('statement.languages.help')}</p>
        </HelpPopover>
      </div>
      <CheckboxGroup
        aria-labelledby="decl-idiomas"
        value={selected}
        onChange={(values) => {
          const next = values.filter(isStatementLang);
          // At least one language must stay selected.
          if (next.length === 0) return;
          onChange(uniqueLangs(next));
        }}
        isDisabled={!mayEdit}
        className="lang-cards"
      >
        {STATEMENT_LANGS.map((code) => (
          <Checkbox key={code} value={code} className="lang-card">
            <CheckIcon aria-hidden="true" className="lang-check" />
            <span lang={code}>{t(`statement.languages.${code}`)}</span>
            <span className="sr-only">
              {' '}
              ({statusOf(code)}
              {detected.has(code) ? `, ${t('statement.languages.detected')}` : ''})
            </span>
          </Checkbox>
        ))}
      </CheckboxGroup>
      <p className="text-sm text-ink-muted" aria-live="polite">
        {selected.length === 1
          ? t('statement.languages.selectedOne', { name: list })
          : t('statement.languages.selectedMany', { list, count: selected.length })}
      </p>
      {removingApproved && (
        <p className="text-sm text-ink-muted">{t('statement.languages.removeApproved')}</p>
      )}
      {unsupported.length > 0 && (
        <p className="text-sm text-ink-muted">
          {t('statement.languages.unsupported', { list: unsupported.join(', ') })}
        </p>
      )}
      {error && <Message tone="error">{error}</Message>}
      <div>
        <Button onPress={onNext} isPending={saving} isDisabled={selected.length < 1}>
          {t('statement.languages.next')}
        </Button>
      </div>
    </section>
  );
}

/** The «Declaración de accesibilidad» tab of a website. */
export function StatementTab() {
  const { t, i18n } = useTranslation();
  const { siteId } = useParams({ from: '/sites/$siteId' });
  const queryClient = useQueryClient();
  const { data: me } = useQuery(meQuery);
  const site = useQuery(siteQuery(siteId)).data;
  const languages = useQuery(statementLanguagesQuery(siteId));
  const chosen = languages.data?.languages ?? [];
  const mayEdit = can(me, 'site', 'update');
  /** Languages marked in step 1; null until the list from the server (or the detection) is known. */
  const [selected, setSelected] = useState<StatementLang[] | null>(null);
  /** Which language's preview the following steps show (the data is the same for all). */
  const [previewLang, setPreviewLang] = useState<StatementLang | null>(null);
  const [step, setStep] = useState<StepId | null>(null);
  // Once the data step has been opened, it stays reachable from the language step.
  const [pastLanguage, setPastLanguage] = useState(false);
  const [focusNonce, setFocusNonce] = useState(0);
  const [saving, setSaving] = useState(false);
  const first = isStatementLang(i18n.language) ? i18n.language : 'es';
  const approved = new Set(chosen.filter((entry) => entry.version !== null).map((entry) => entry.lang));
  const detectedSupported = (languages.data?.detected ?? [])
    .map((entry) => entry.lang)
    .filter(isStatementLang);
  const fallback: StatementLang =
    chosen.find((entry) => entry.lang === first)?.lang ?? detectedSupported[0] ?? chosen[0]?.lang ?? first;
  const active =
    (previewLang && selected?.includes(previewLang) ? previewLang : null) ?? selected?.[0] ?? fallback;
  const view = useQuery({
    ...statementQuery(siteId, active),
    enabled: languages.isSuccess,
    refetchOnWindowFocus: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
  const [languageError, setLanguageError] = useState<string | null>(null);
  usePageTitle(t('statement.tab'), site?.name);

  useEffect(() => {
    if (!languages.data) return;
    if (selected === null) {
      const fromServer = languages.data.languages.map((entry) => entry.lang).filter(isStatementLang);
      const detectedNow = languages.data.detected.map((entry) => entry.lang).filter(isStatementLang);
      const initial = fromServer.length > 0 ? fromServer : detectedNow.length > 0 ? detectedNow : [fallback];
      setSelected(uniqueLangs(initial));
      setPreviewLang(initial.includes(fallback) ? fallback : initial[0]!);
    }
    if (step !== null || !view.data) return;
    if (view.data.lang !== active) return;
    const anyWork = languages.data.languages.some((entry) => entry.hasDraft || entry.version !== null);
    const next: StepId = !anyWork
      ? 'language'
      : view.data.sections.length === 0
        ? 'data'
        : view.data.status === 'pendiente'
          ? 'review'
          : 'validate';
    if (next !== 'language') setPastLanguage(true);
    setStep(next);
  }, [languages.data, selected, fallback, step, view.data, active]);

  async function changeLanguages(next: StatementLang[]) {
    setLanguageError(null);
    setSaving(true);
    try {
      const updated = await setStatementLanguages(siteId, next);
      queryClient.setQueryData(statementLanguagesQuery(siteId).queryKey, updated);
      // Drop cached views of languages that no longer exist.
      for (const entry of chosen) {
        if (!next.includes(entry.lang)) {
          queryClient.removeQueries({ queryKey: statementQuery(siteId, entry.lang).queryKey });
        }
      }
      announce(t('statement.languages.updated'));
      return true;
    } catch {
      setLanguageError(t('statement.errors.generic'));
      return false;
    } finally {
      setSaving(false);
    }
  }

  function pickSelected(next: StatementLang[]) {
    if (next.length === 0) return;
    const langs = uniqueLangs(next);
    setSelected(langs);
    setPreviewLang((current) => (current && langs.includes(current) ? current : langs[0]!));
  }

  async function commit(target: EditorStep) {
    if (saving || !selected || selected.length === 0) return;
    const nextLangs = uniqueLangs(selected);
    if (
      mayEdit &&
      !sameLangs(
        nextLangs,
        chosen.map((entry) => entry.lang),
      )
    ) {
      const ok = await changeLanguages(nextLangs);
      if (!ok) return;
    }
    setPastLanguage(true);
    setStep(target);
    setFocusNonce((nonce) => nonce + 1);
  }

  function openStep(target: StepId) {
    if (target === step || saving) return;
    if (target === 'language') {
      setStep('language');
      setFocusNonce((nonce) => nonce + 1);
      return;
    }
    if (step === 'language') {
      void commit(target);
      return;
    }
    setPastLanguage(true);
    setStep(target);
    setFocusNonce((nonce) => nonce + 1);
  }

  function goTo(target: EditorStep) {
    setPastLanguage(true);
    setStep(target);
    setFocusNonce((nonce) => nonce + 1);
  }

  const statusOf = (code: StatementLang) => {
    const entry = chosen.find((item) => item.lang === code);
    return entry?.version
      ? t('statement.published', { n: entry.version })
      : entry?.hasDraft
        ? t('statement.draft')
        : t('statement.none');
  };
  const unsupported = (languages.data?.detected ?? [])
    .map((entry) => entry.lang)
    .filter((code) => !isStatementLang(code));
  const hasText = Boolean(view.data && view.data.lang === active && view.data.sections.length > 0);
  const reached = hasText ? 3 : pastLanguage ? 1 : 0;
  const editorStep: EditorStep = step === 'review' || step === 'validate' || step === 'data' ? step : 'data';
  const editorView = view.data?.lang === active ? view.data : null;
  const pendingLangs = (selected ?? chosen.map((entry) => entry.lang))
    .filter(isStatementLang)
    .filter((code) => !chosen.some((entry) => entry.lang === code && entry.version !== null));

  return (
    <section aria-labelledby="declaracion" className="statement-tab">
      <h2 id="declaracion" className="text-2xl font-bold">
        {t('statement.title')}
      </h2>
      <p className="max-w-3xl">{t('statement.intro')}</p>
      {step && <Stepper step={step} onStep={openStep} reached={reached} />}
      {languages.isError && <Message tone="error">{t('errors.generic')}</Message>}
      {view.isError && <Message tone="error">{t('errors.generic')}</Message>}
      {step === null && !languages.isError && !view.isError && <p role="status">{t('app.loading')}</p>}
      {step === 'language' && selected && (
        <LanguageStep
          detectedCount={languages.data?.detected.length ?? 0}
          detected={new Set(detectedSupported)}
          selected={selected}
          approved={approved}
          statusOf={statusOf}
          mayEdit={mayEdit}
          saving={saving}
          unsupported={unsupported}
          error={languageError}
          focusNonce={focusNonce}
          onChange={pickSelected}
          onNext={() => void commit('data')}
        />
      )}
      {editorStep && !editorView && !view.isError && step !== 'language' && step !== null && (
        <p role="status">{t('app.loading')}</p>
      )}
      {editorView && step !== null && (
        <div hidden={step === 'language'}>
          <Editor
            key={active}
            siteId={siteId}
            lang={active}
            initial={editorView}
            mayEdit={mayEdit}
            step={editorStep}
            onStep={goTo}
            active={step !== 'language'}
            focusNonce={focusNonce}
            pendingLangs={pendingLangs}
          />
        </div>
      )}
    </section>
  );
}
